import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getVariantsBySearch, ProductGroupLoadingKeys } from '../../slices/productGroupSlice';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import { getStatusColor } from '../StatusSelect';
import { enumInventoryPhasedStatus } from '../../consts/inventoryStatus';
import { useDebounce } from '../../helpers/useDebounce';

const VariantModal = ({
  isOpen,
  onClose,
  onAddVariant,
  isReplenishable,
  defaultSearchTerm
}) => {
  const dispatch = useDispatch();

  const [variantSearchTerm, setVariantSearchTerm] = useState(defaultSearchTerm || '');
  const currentVariantSearch = useDebounce(variantSearchTerm, 500);

  const {
    loading: groupLoading,
    loadingKeys: groupLoadingKeys,
    searchedVariants: searchedVariantsByGroup,
  } = useSelector((state) => state.productGroup);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(getVariantsBySearch({
      search: currentVariantSearch,
      isReplenishable: isReplenishable
    }));
  }, [currentVariantSearch, isReplenishable, dispatch, isOpen]);

  const searchedVariantsIsLoading = () => 
    groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getVariants);
  const addVariantIsLoading = () =>
    groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.addVariantToGroup);

  const handleAddVariant = async (variant) => {
    await onAddVariant(variant);
    setVariantSearchTerm('');
  };

  const handleClose = () => {
    onClose();
    setVariantSearchTerm('');
  };

  // Handle Esc key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Add Variant</h3>
                <div className="mt-4">
                  <div className="mb-4">
                    <label htmlFor="component-search" className="block text-sm font-medium text-gray-700">
                      Search Variants
                    </label>
                    <div className="relative mt-1">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        id="component-search"
                        className="block w-full border rounded-md border-gray-300 pl-10 py-2 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        placeholder="Search by name, SKU, or category"
                        value={variantSearchTerm}
                        onChange={(e) => setVariantSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="mt-2 max-h-60 overflow-y-auto border rounded-md relative">
                    {(searchedVariantsIsLoading() || addVariantIsLoading()) && <OverlayGrayBackground />}
                    {searchedVariantsByGroup.length > 0 ? (
                      <ul className="divide-y divide-gray-200">
                        {searchedVariantsByGroup.map((variant) => (
                          <li 
                            key={variant.id}
                            className="cursor-pointer px-4 py-3 hover:bg-gray-50"
                            onClick={async () => {
                              await handleAddVariant(variant)
                            }}
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                await handleAddVariant(variant);
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Add component ${variant.name}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{variant.name}</p>
                                <p className="text-sm text-gray-500">SKU: {variant.sku}</p>
                              </div>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(variant.status)}`}>
                                {enumInventoryPhasedStatus[variant.status]}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex items-center justify-center py-8">
                        <p className="text-gray-500">No variants found</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default VariantModal; 